import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {Button, Checkbox, DatePicker, Flex, Form, Input, message, Modal, Popconfirm, Table, Tag, Tooltip, Upload} from 'antd';
import {createStyles} from 'antd-style';
import dayjs from "dayjs";
import IntlMessage from "../../../../../components/IntlMessage/IntlMessage";
import MyText from "../../../../../components/UI/Text/MyText";
import classes from "../../Applications/application.module.css";
import JournalService from "../../../../../services/JournalService";
import GroupService from "../../../../../services/GroupService";
import {getJournal} from "../../../../../store/slices/TeacherGroupSlice";
import BlockLoading from "../../../../../components/LoadingComponents/BlockLoading";
import FormItem from "antd/es/form/FormItem";
import {EditOutlined, PlusOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import GreenButton from "../../../../../components/UI/Buttons/GreenButton";
import GreyButton from "../../../../../components/UI/Buttons/GreyButton";
import StudentScheduleModal from "../../../../../components/UI/StudentScheduleModal/StudentScheduleModal";

const useStyle = createStyles(({css, token}) => {
    const {antCls} = token;
    return {
        customTable: css`
            ${antCls}-table {
                ${antCls}-table-container {
                    ${antCls}-table-body,
                    ${antCls}-table-content {
                        scrollbar-width: thin;
                        scrollbar-color: #eaeaea transparent;
                        scrollbar-gutter: stable;
                    }
                }
            }
        `,
    };
});
const EditableCell = ({
                          editing,
                          dataIndex,
                          title,
                          inputType,
                          record,
                          index,
                          children,
                          ...restProps
                      }) => {
    const inputNode = inputType === 'date' ? <Input type={'date'}/> : <Input/>;
    return (
        <td {...restProps}>
            {editing ? (
                <Form.Item
                    name={dataIndex}
                    style={{margin: 0}}
                    rules={[
                        {
                            required: true,
                            message: <IntlMessage id={"tg.emptyInput"}/>,
                        },
                    ]}
                >
                    {inputNode}
                </Form.Item>
            ) : (
                children
            )}
        </td>
    );
};
// onEnrollmentsChange — отчисление/восстановление: сообщает TeacherGroupPage, чтобы вкладка оценок перечитала статусы
const JournalPage = ({onEnrollmentsChange}) => {
    const {t} = useTranslation();

    const {id} = useParams()
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {journal, isLoading} = useSelector(state => state.teacherGroup);
    const {user} = useSelector(state => state.user);

    const [form] = Form.useForm();
    const name = Form.useWatch('name', form);
    const date = Form.useWatch('date', form);
    const moduleName = Form.useWatch('moduleName', form);

    const [formAttendance] = Form.useForm();
    const attendanceValues = Form.useWatch([], formAttendance); // следим за всей формой

    const [reasonForm] = Form.useForm();
    const reason = Form.useWatch('reason', reasonForm);
    const raasonValues = Form.useWatch([], reasonForm); // следим за всей формой


    const [editingKey, setEditingKey] = useState('');
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedAttendances, setSelectedAttendances] = useState([]);

    const [openNoteLessonModal, setOpenNoteLessonModal] = useState(false)
    const [noteLesson, setNoteLesson] = useState(null)
    const [isViewNote, setIsViewNote] = useState(false)
    const isEditing = record => record.key === editingKey;
    const getAttendanceFieldName = id => `attendance-${id}`;

    const [lessonAddForm] = Form.useForm();
    const lessonsForJournals = Form.useWatch('lessonsForJournals', lessonAddForm);
    const lessonNumberAddLesson = Form.useWatch('lessonNumberAddLesson', lessonAddForm);
    const topicNameAddLesson = Form.useWatch('topicNameAddLesson', lessonAddForm);
    const moduleNameAddLesson = Form.useWatch('moduleNameAddLesson', lessonAddForm);
    const lessonDateAddLesson = Form.useWatch('lessonDateAddLesson', lessonAddForm);

    const [uploadedFile, setUploadedFile] = useState(null);


    const [openAddLessonModal, setOpenAddLessonModal] = useState(false)
    const [isAdminEditMode, setIsAdminEditMode] = useState(false)

    // п.7: даты присоединения учеников (studentId -> 'YYYY-MM-DD') для пометки ☁️
    const [joinDates, setJoinDates] = useState({});
    // ТЗ updated п.8: порог низкой посещаемости (глобальная настройка)
    const [lowThreshold, setLowThreshold] = useState(50);
    // Отчисление: studentId -> {date: 'YYYY-MM-DD', reason}. Занятия с даты отчисления (и без даты)
    // серые и закрыты для отметок, в процент посещаемости не идут
    const [leaveDates, setLeaveDates] = useState({});
    const isAdmin = user?.role?.roleName === 'admin';
    const [expelStudent, setExpelStudent] = useState(null); // {id, name} — открыта модалка отчисления
    const [expelForm] = Form.useForm();
    const [isExpelSaving, setIsExpelSaving] = useState(false);

    // Отчисление: урок «после отчисления» — дата урока >= даты отчисления; урок без даты тоже
    // считаем закрытым (иначе будущие уроки отчисленного выглядели бы обычными, когда им проставят дату)
    function isAfterLeave(studentId, lesson) {
        const leaveDate = leaveDates[studentId]?.date;
        if (!leaveDate) return false;
        return !lesson?.date || String(lesson.date).slice(0, 10) >= leaveDate;
    }

    // 'YYYY-MM-DD' -> 'DD.MM.YYYY' без Date(), чтобы не словить таймзонный сдвиг
    const formatLeaveDate = d => String(d).slice(0, 10).split('-').reverse().join('.');

    /**
     * ТЗ updated п.8: процент посещаемости ученика.
     * fix joinDate: урок попадает в знаменатель, если у него есть дата
     * И (дата урока >= joinDate ИЛИ преподаватель явно отметил "был").
     * Отсутствие строки Attendance трактуем как "не был" — урок всё равно в знаменателе
     * (раньше такой урок молча выпадал и завышал процент).
     * Знаменатель 0 → null (рендер такой процент просто не показывает).
     */
    function getAttendancePercent(studentId) {
        const joinDate = joinDates[studentId];
        let total = 0;
        let present = 0;
        (journal?.lessons || []).forEach(lesson => {
            if (!lesson.date) return;
            const lessonDate = String(lesson.date).slice(0, 10);
            const attendance = (lesson.attendances || []).find(a => a?.user?.id === studentId);
            const isPresent = attendance?.isPresent === true;
            // занятие до прихода ученика с него не требуем — но отмеченное "был" засчитываем
            if (joinDate && lessonDate < joinDate && !isPresent) return;
            // отчисление: занятия с даты отчисления не требуем (зеркально joinDate)
            if (isAfterLeave(studentId, lesson) && !isPresent) return;
            total++;
            if (isPresent) present++;
        });
        if (total === 0) return null;
        return {percent: Math.round((present * 100) / total), present, total};
    }

    // п.10: модалка полного расписания ученика
    const [openSchedule, setOpenSchedule] = useState(false);
    const [scheduleStudent, setScheduleStudent] = useState(null);

    function loadEnrollments(groupId) {
        GroupService.getGroupEnrollments(groupId)
            .then((d) => {
                const map = {};
                const leaveMap = {};
                (d?.enrollments || []).forEach(e => {
                    // п.7: строка 'YYYY-MM-DD' + сравнение по календарной дате — без таймзонного off-by-one
                    if (e.joinDate) map[e.studentId] = String(e.joinDate).slice(0, 10);
                    // отчисление: отдельно от проверки joinDate — joinDate может быть null
                    if (e.leaveDate) leaveMap[e.studentId] = {
                        date: String(e.leaveDate).slice(0, 10),
                        reason: e.leaveReason || null
                    };
                });
                setJoinDates(map);
                setLeaveDates(leaveMap);
                if (d?.lowAttendanceThreshold != null) setLowThreshold(Number(d.lowAttendanceThreshold));
            })
            .catch(() => {
                setJoinDates({});
                setLeaveDates({});
            });
    }

    useEffect(() => {
        if (!journal?.group?.id) return;
        loadEnrollments(journal.group.id);
    }, [journal?.group?.id]);

    // Отчисление (admin): членство, посещаемость и оценки сохраняются, занятия с даты становятся серыми
    async function saveExpel() {
        const values = await expelForm.validateFields().catch(() => null);
        if (!values || !expelStudent?.id || !journal?.group?.id) return;
        setIsExpelSaving(true);
        await GroupService.expelStudent(journal.group.id, expelStudent.id,
            values.leaveDate.format('YYYY-MM-DD'), (values.leaveReason || '').trim())
            .then(() => {
                message.success(<IntlMessage id="success"/>, 5);
                setExpelStudent(null);
                expelForm.resetFields();
                loadEnrollments(journal.group.id);
                onEnrollmentsChange?.();
            })
            .catch((err) => {
                const serverText = typeof err?.response?.data === 'string' ? err.response.data : null;
                message.error(serverText || <IntlMessage id="error"/>, 5);
            })
            .finally(() => setIsExpelSaving(false));
    }

    async function restoreStudent(studentId) {
        await GroupService.restoreStudent(journal.group.id, studentId)
            .then(() => {
                message.success(<IntlMessage id="success"/>, 5);
                loadEnrollments(journal.group.id);
                onEnrollmentsChange?.();
            })
            .catch((err) => {
                const serverText = typeof err?.response?.data === 'string' ? err.response.data : null;
                message.error(serverText || <IntlMessage id="error"/>, 5);
            });
    }

    function formatDateDDMMYYYY(dateInput) {
        const date = new Date(dateInput);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Месяцы с 0
        const year = date.getFullYear();

        return `${year}-${month}-${day}`;
    }

    const edit = record => {

        const initValues = {
            ...record,
            date: record.date ? formatDateDDMMYYYY(record.date) : null,
        };

        form.setFieldsValue(initValues);
        setEditingKey(record.key);
    };
    const cancel = () => {
        setEditingKey('');
    };
    const save = async record => {
        form.validateFields().then(async () => {
            await JournalService.updateLesson(record.key, date, record.journal_id, name)
                .then(() => {
                    message.success(<IntlMessage id={'success'}/>, 5);
                    form.resetFields();
                })
                .catch(() => {
                    message.error(<IntlMessage id={'error'}/>, 5);
                })
                .finally(() => {
                    dispatch(getJournal({id}));
                    setEditingKey('');
                })
        })
            .catch(() => {
                console.log("Validation error!")
            })
    };

    const updateLesson = async key => {
        await JournalService.updateLesson(key)
            .then(() => {
                message.success(<IntlMessage id={'success'}/>, 5);
                form.resetFields();
            })
            .catch(() => {
                message.error(<IntlMessage id={'error'}/>, 5);
            })
            .finally(() => {
                dispatch(getJournal({id}));
                setEditingKey('');
            })
    }

    const {styles} = useStyle();
    const baseColumn = {
        title: <MyText center><IntlMessage id={"fullName"}/></MyText>,
        width: 200,
        dataIndex: 'name',
        key: 'name',
        fixed: 'left',
        // п.10: клик по ФИО открывает полное расписание ученика по всем дисциплинам
        // ТЗ updated п.8: рядом — процент посещаемости, низкий выделяется красным с ⚠️
        render: (_, record) => {
            const attendance = getAttendancePercent(record.studentId);
            // отчисление: отчисленного не подсвечиваем как «низкую посещаемость», имя серое
            const leave = leaveDates[record.studentId];
            const isLow = !leave && attendance && attendance.percent < lowThreshold;
            return (
                <Flex vertical gap={4} align={"flex-start"}>
                    <span
                        style={{
                            cursor: 'pointer',
                            color: leave ? '#8c8c8c' : isLow ? '#cf1322' : '#1677ff',
                            fontWeight: isLow ? 600 : 'normal'
                        }}
                        title={attendance ? `Посещаемость: ${attendance.percent}% (${attendance.present} из ${attendance.total})` : undefined}
                        onClick={() => {
                            setScheduleStudent({id: record.studentId, name: record.name});
                            setOpenSchedule(true);
                        }}
                    >
                        {isLow && '⚠️ '}{record.name}
                        {attendance && <span style={{color: isLow ? '#cf1322' : '#8c8c8c'}}> — {attendance.percent}%</span>}
                    </span>
                    {leave &&
                        <Tooltip title={leave.reason ? t('groups.leaveReasonTooltip', {reason: leave.reason}) : undefined}>
                            <Tag color="default" style={{marginInlineEnd: 0, cursor: leave.reason ? 'help' : 'default'}}>
                                <IntlMessage id={'groups.expelledFrom'} values={{date: formatLeaveDate(leave.date)}}/>
                            </Tag>
                        </Tooltip>
                    }
                    {isAdmin && (leave ? (
                        <Popconfirm
                            title={t('groups.restore')}
                            description={t('groups.restoreConfirm')}
                            onConfirm={() => restoreStudent(record.studentId)}
                            okText={t('yes')}
                            cancelText={t('no')}
                        >
                            <Button type="link" size="small" style={{padding: 0, height: 'auto'}}>
                                <span><IntlMessage id={'groups.restore'}/></span>
                            </Button>
                        </Popconfirm>
                    ) : (
                        <Button type="link" size="small" danger style={{padding: 0, height: 'auto'}}
                                onClick={() => {
                                    expelForm.setFieldsValue({leaveDate: dayjs(), leaveReason: ''});
                                    setExpelStudent({id: record.studentId, name: record.name});
                                }}>
                            <span><IntlMessage id={'groups.expel'}/></span>
                        </Button>
                    ))}
                </Flex>
            );
        },
    };
    const lessonColumns = journal?.lessons
        ?.filter(lesson => lesson.date !== null) // ✅ отсекаем null-даты
        .map(lesson => ({
            title: (
                <Flex style={{cursor: "pointer"}} align={"center"} justify={"center"} gap={"small"} onClick={() => {
                    // fix joinDate: поля незамонтированных ячеек в форме отсутствуют (undefined),
                    // из-за чего every() всегда давал false и колонку можно было только проставить,
                    // но не снять. Считаем и переключаем ТОЛЬКО реально существующие в форме поля.
                    const currentValues = formAttendance.getFieldsValue();
                    const existingIds = (lesson.attendances || [])
                        // отчисление: клетки отчисленного после даты отчисления не переключаем
                        .filter(att => !isAfterLeave(att?.user?.id, lesson))
                        .map(att => getAttendanceFieldName(att.id))
                        .filter(fieldName => Object.prototype.hasOwnProperty.call(currentValues, fieldName));
                    if (existingIds.length === 0) return;
                    const allChecked = existingIds.every(fieldName => !!currentValues[fieldName]);
                    existingIds.forEach(fieldName => {
                        formAttendance.setFieldValue(fieldName, !allChecked);
                    });
                }}>
                    <MyText center>
                        {new Date(lesson.date).toLocaleDateString("ru-RU")}
                    </MyText>
                </Flex>
            ),
            dataIndex: lesson.number,
            key: lesson.number
        })) || [];

    const columns1 = [baseColumn, ...lessonColumns];
    const columns2 = [
        {
            title: <MyText center><IntlMessage id={"tg.dateOfLesson"}/></MyText>,
            width: '20%',
            dataIndex: 'date',
            key: 'date',
            editable: true,
            render: (_, record) => (
                <p>
                    {record.date ? new Date(record.date).toLocaleDateString('kk-KZ') : ""}
                </p>
            ),
        },
        {
            title: <MyText center><IntlMessage id={"tg.nameOfLesson"}/></MyText>,
            width: '30%',
            dataIndex: 'name',
            key: 'name',
            editable: true
        },
        {
            title: <MyText center><IntlMessage id={"tg.moduleOfLesson"}/></MyText>,
            width: '30%',
            dataIndex: 'moduleName',
            key: 'moduleName',
            editable: false
        },
        {
            title: <MyText center><IntlMessage id={"actions"}/></MyText>,
            key: 'action',
            fixed: 'right',
            width: '20%',
            render: (_, record) => {
                const editable = isEditing(record);
                return record.isDone ? <IntlMessage id={'finished'}/>
                    : editable ? (
                        <span>
                        <p onClick={() => save(record)} className={classes.copyLinkBtn}>
                        <IntlMessage id={'save'}/>
                        </p>
                        <p onClick={() => cancel()} className={classes.rejectBtn}>
                        <IntlMessage id={'cancel'}/>
                        </p>
                    </span>
                    ) : (
                        <span>
                         <p onClick={() => edit(record)} className={classes.copyLinkBtn}>
                        <IntlMessage id={'edit'}/></p>
                            {(journal?.group?.category?.nameEng !== 'General English' && journal?.group?.category?.nameEng !== 'Mathematics') &&
                                record.date !== null &&
                                <Popconfirm
                                    title={t('finishLesson')}
                                    description={t('youSureToFinishLesson')}
                                    onConfirm={() => {
                                        updateLesson(record.key)
                                    }}
                                    // className={classes.deleteBlock}
                                    okText={t('yes')}
                                    cancelText={t('no')}
                                >
                                    <p className={classes.copyLinkBtn}>
                                        <IntlMessage id={'finishLesson'}/></p>
                                </Popconfirm>
                            }
                    </span>
                    );

            },
        },
    ];
    const mergedColumns = columns2.map(col => {
        if (!col.editable) return col;

        return {
            ...col,
            onCell: record => ({
                record,
                inputType: col.dataIndex === 'date' ? 'date' : 'text',
                dataIndex: col.dataIndex,
                title: col.title,
                editing: isEditing(record),
            }),
        };
    });
    // ТЗ updated п.10: порядок студентов в группе в БД не определён — сортируем по ФИО,
    // чтобы журнал, оценки и списки шли одинаково по алфавиту
    const sortedStudents = [...(journal?.group?.students || [])].sort((a, b) =>
        (a?.profile?.fullName || '').localeCompare(b?.profile?.fullName || '', 'kk')
    );
    const dataSource1 = sortedStudents.map((student, index) => {
        const row = {
            key: String(index + 1),
            name: `${student?.profile?.fullName}`, // если имя разбито
            studentId: student.id,
        };

        const studentJoinDate = joinDates[student.id]; // 'YYYY-MM-DD' | undefined

        journal.lessons?.forEach((lesson) => {
            const attendance = lesson.attendances.find(a => a.user.id === student.id);
            // fix joinDate: урок до присоединения — теперь ТОЛЬКО пометка ☁️, ввод не блокируем.
            // Раньше здесь стоял ранний return, из-за него ячейка была мёртвой и посещаемость
            // добавленным позже ученикам нельзя было отметить вообще.
            const beforeJoin = !!(studentJoinDate && lesson?.date
                && String(lesson.date).slice(0, 10) < studentJoinDate);
            // Разрешаем редактирование если: урок не завершен ИЛИ (урок завершен И админ в режиме редактирования)
            const canEdit = lesson?.date !== null && (!lesson?.isDone || (lesson?.isDone && isAdminEditMode && user?.role?.roleName === 'admin'));
            // отчисление: занятие с даты отчисления — серая клетка только для чтения при любом canEdit
            // (FormItem не монтируется, поэтому клетка не попадает ни в «отметить всех», ни в сохранение)
            const afterLeave = isAfterLeave(student.id, lesson);

            if (afterLeave) {
                row[lesson.number] =
                    <div title={t('tg.afterLeave')}
                         style={{
                             textAlign: 'center',
                             background: '#f0f0f0',
                             color: '#bfbfbf',
                             borderRadius: 4,
                             padding: '0 6px',
                             cursor: 'not-allowed'
                         }}>
                        {attendance?.isPresent ? '✅' : '—'}
                    </div>
            } else if (canEdit && !attendance?.id) {
                // fix joinDate: у ученика нет строки Attendance для этого урока — сохранять нечего.
                // Рисовать здесь чекбокс нельзя: имя поля выходит 'attendance-undefined', оно ОДНО
                // на все такие клетки таблицы (галочки ставятся пачкой), а из payload такой ключ
                // выкидывается — преподаватель видела бы «Успешно» и потерянные отметки, то есть
                // ровно ту жалобу, которую мы чиним. Показываем явную пометку вместо ложного ввода.
                row[lesson.number] =
                    <span title="Нет записи посещаемости: обратитесь к администратору, чтобы переоткрыть ученику доступ к журналу"
                          style={{cursor: "help"}}>⚠️</span>
            } else if (canEdit) {
                row[lesson.number] =
                    <Flex gap={"middle"} justify={"center"} align={"center"}
                          style={{textAlign: 'center', opacity: beforeJoin ? 0.65 : 1}}>
                        <FormItem valuePropName={"checked"} name={getAttendanceFieldName(attendance.id)}
                                  style={{marginBottom: 0}}
                                  initialValue={attendance.isPresent}>
                            <Checkbox/>
                        </FormItem>
                        {beforeJoin && <span title="До присоединения" style={{fontSize: 12}}>☁️</span>}
                        <EditOutlined onClick={() => {
                            setNoteLesson(attendance);
                            setOpenNoteLessonModal(true);
                            reasonForm.setFieldsValue({
                                reason: attendance?.reasonOfAbsent || ''
                            });
                        }} style={{cursor: "pointer"}}/>
                    </Flex>
            } else {
                // fix joinDate: за занятия до прихода ученика не пугаем преподавателя крестом — рисуем ☁️
                const notPresentMark = beforeJoin ? <span title="До присоединения">☁️</span> : '❌';
                row[lesson.number] = attendance ? (attendance.isAbsentForReason ? <span
                    style={{cursor: "pointer"}}
                    onClick={() => {
                        setIsViewNote(true);
                        setOpenNoteLessonModal(true);
                        reasonForm.setFieldsValue({
                            reason: attendance?.reasonOfAbsent || ''
                        });
                    }}>✴️</span> : (attendance.isPresent ? '✅' : notPresentMark))
                    : (beforeJoin ? <span title="До присоединения">☁️</span> : '-');
            }
        });

        return row;
    }) || [];

    const dataSource2 = journal?.lessons?.map((lesson, index) => {
        const row = {
            key: lesson.id,
            date: lesson?.date,
            journal_id: lesson?.journalTopic.id,
            // : <MyText center>{new Date(lesson.date).toLocaleDateString()}</MyText>,
            name: `${lesson?.journalTopic?.name}`,
            moduleName: `${lesson?.journalTopic?.moduleName}`,
            isDone: lesson.isDone
        };
        return row;
    }) || [];

    const tableRef = useRef();

    useEffect(() => {
        const tableContainer = tableRef.current?.querySelector('.ant-table-content');
        if (!tableContainer) return;

        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;

        const onMouseDown = (e: MouseEvent) => {
            isDown = true;
            startX = e.pageX - tableContainer.offsetLeft;
            scrollLeft = tableContainer.scrollLeft;
            tableContainer.style.cursor = 'grabbing';
            tableContainer.style.userSelect = 'none';
        };

        const onMouseLeave = () => {
            isDown = false;
            tableContainer.style.cursor = '';
            tableContainer.style.userSelect = '';
        };

        const onMouseUp = () => {
            isDown = false;
            tableContainer.style.cursor = '';
            tableContainer.style.userSelect = '';
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - tableContainer.offsetLeft;
            const walk = (x - startX) * 1; // скорость прокрутки
            tableContainer.scrollLeft = scrollLeft - walk;
        };

        tableContainer.addEventListener('mousedown', onMouseDown);
        tableContainer.addEventListener('mouseleave', onMouseLeave);
        tableContainer.addEventListener('mouseup', onMouseUp);
        tableContainer.addEventListener('mousemove', onMouseMove);

        return () => {
            tableContainer.removeEventListener('mousedown', onMouseDown);
            tableContainer.removeEventListener('mouseleave', onMouseLeave);
            tableContainer.removeEventListener('mouseup', onMouseUp);
            tableContainer.removeEventListener('mousemove', onMouseMove);
        };
    }, []);

    const initialAttendanceValues = {};

    useEffect(() => {
        if (!journal) return;

        const initialValues = {};
        journal.lessons?.forEach(lesson => {
            lesson.attendances.forEach(att => {
                initialValues[`attendance-${att.id}`] = att.isPresent;
            });
        });

        formAttendance.setFieldsValue(initialValues); // обновляем форму вручную
    }, [journal]);

    async function saveAttendances() {
        const values = formAttendance.getFieldsValue(); // { attendance-id: true/false }
        // отчисление: отметки отчисленных за занятия с даты отчисления не отправляем
        // (бэкенд их тоже молча пропускает, это страховка от «Успешно» без записи)
        const afterLeaveIds = new Set();
        (journal?.lessons || []).forEach(lesson => (lesson.attendances || []).forEach(att => {
            if (att?.id && isAfterLeave(att?.user?.id, lesson)) afterLeaveIds.add(String(att.id));
        }));


        // const selectedIds = Object.entries(values)
        //     .filter(([key, value]) => key.startsWith('attendance-') && value)
        //     .map(([key]) => key.replace('attendance-', ''));
        const attendanceMap = Object.entries(values)
            .filter(([key, value]) => key.startsWith('attendance-') && value !== null && value !== undefined)
            .reduce((acc, [key, value]) => {
                const id = key.replace('attendance-', '');
                // fix joinDate: ключ 'attendance-undefined' появляется, когда у ученика нет строки
                // Attendance для урока — сервер на таком id роняет весь запрос в 400, выкидываем его
                if (!id || id === 'undefined' || id === 'null') return acc;
                if (afterLeaveIds.has(id)) return acc;
                acc[id] = value;
                return acc;
            }, {});

        await JournalService.saveAttendances(attendanceMap)
            .then(() => {
                message.success(<IntlMessage id="success"/>, 5);
                // fix joinDate: сброс формы и перечитывание журнала — ТОЛЬКО при успехе.
                // В .finally() неудачное сохранение стирало всё, что преподаватель отметила.
                formAttendance.resetFields(); // сбросить форму (чекбоксы)
                setIsAdminEditMode(false); // выключаем режим редактирования админа
                dispatch(getJournal({id}));
            })
            .catch((err) => {
                // fix joinDate: показываем текст ошибки сервера, если он есть; форму не трогаем
                const serverText = typeof err?.response?.data === 'string' ? err.response.data : null;
                message.error(serverText || <IntlMessage id="error"/>, 5);
            });
    }

    // fix joinDate: причина приходит аргументом — пустая строка СНИМАЕТ уважительную причину на бэке
    async function makeAbsentForReason(reasonValue = reason) {
        // fix joinDate: id забираем до сброса состояния, чтобы не зависеть от порядка setNoteLesson(null)
        const attendanceId = noteLesson?.id;
        if (!attendanceId) return;
        const isClearing = !reasonValue || String(reasonValue).trim() === '';

        // fix joinDate: снимать причину имеет право только кнопка «Снять причину». Пустая заметка
        // у ученика без причины раньше уходила на бэк и молча гасила отметку «был» — просто закрываем.
        if (isClearing && !noteLesson?.isAbsentForReason) {
            setOpenNoteLessonModal(false);
            setNoteLesson(null);
            setIsViewNote(false);
            reasonForm.resetFields();
            return;
        }

        await JournalService.makeAbsentForReason(attendanceId, isClearing ? '' : reasonValue)
            .then(() => {
                message.success(<IntlMessage id="success"/>, 5);
                // fix joinDate: при снятии причины галочка гаснет (false), при простановке — ставится
                formAttendance.setFieldValue(getAttendanceFieldName(attendanceId), !isClearing);
            })
            .catch((err) => {
                const serverText = typeof err?.response?.data === 'string' ? err.response.data : null;
                message.error(serverText || <IntlMessage id="error"/>, 5);
            })
            .finally(() => {
                setOpenNoteLessonModal(false);
                setNoteLesson(null);
                setIsViewNote(false)
                reasonForm.resetFields();
                // dispatch(getJournal({id}));
            });
    }

    async function finishAllLessons() {
        message.loading(<IntlMessage id={'changing'}/>, 0);
        await JournalService.finishAllLessons(id)
            .then(() => {
                message.destroy();
                message.success(<IntlMessage id="success"/>, 5);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id="error"/>, 5);
            })
            .finally(() => {
                dispatch(getJournal({id}));
            });
    }

    async function saveLessonsToJournal() {
        message.loading(<IntlMessage id={'changing'}/>, 0);
        await JournalService.saveLessonsToJournal(journal.id, lessonsForJournals)
            .then(() => {
                message.destroy();
                message.success(<IntlMessage id="success"/>, 5);
            })
            .catch(() => {
                message.destroy();
                message.error(<IntlMessage id="error"/>, 5);
            })
            .finally(() => {
                dispatch(getJournal({id}));
                setOpenAddLessonModal(false);
                lessonAddForm.resetFields();
            });
    }

    const onSelectAttendanceChange = id => {
        const exists = selectedAttendances.some(a => a === id);
        if (exists) {
            setSelectedAttendances(selectedAttendances.filter(a => a !== id));
        } else {
            setSelectedAttendances([...selectedAttendances, id]);
        }
    };
    // const rowSelection = {
    //     selectedRowKeys,
    //     onChange: onSelectChange,
    // };
    // const hasSelected = selectedRowKeys.length > 0;

    const handleChangeExcel = (info) => {
        setUploadedFile(info.file);
    };

    const propsExcelFile = {
        beforeUpload: () => false,
        onChange: handleChangeExcel,
        multiple: false,
    };

    const handleImportExcelFile = async () => {
        message.loading(<IntlMessage id={'loading'}/>, 0);
        await JournalService.importLessonsByExcel(journal.id, uploadedFile)
            .then(() => {
                message.destroy();
                message.success(<IntlMessage id={'success'}/>, 5)
                setUploadedFile(null);
            })
            .catch()
            .finally(() => {
                dispatch(getJournal({id}));
            })
    }

    return (
        <div style={{width: '100%', display: "flex", gap: 20}}>
            <BlockLoading isLoading={isLoading}/>
            <Form form={formAttendance} initialValues={initialAttendanceValues}
                  style={{display: "flex", flex: 1, width: '50%', flexDirection: "column", gap: 20}}>
                <div ref={tableRef}>
                    <Table
                        bordered
                        className={styles.customTable}
                        pagination={false}
                        columns={columns1}
                        dataSource={dataSource1}
                        scroll={{x: 'max-content'}}
                        size="small"
                    />
                </div>
                <Flex gap={"small"}>
                    <Button type="primary" onClick={saveAttendances}>
                        <IntlMessage id={'save'}/>
                    </Button>
                    {user?.role?.roleName === 'admin' && (
                        <Button
                            type={isAdminEditMode ? "default" : "primary"}
                            onClick={() => setIsAdminEditMode(!isAdminEditMode)}
                        >
                            {isAdminEditMode ? <IntlMessage id={'cancel'}/> : <IntlMessage id={'edit'}/>}
                        </Button>
                    )}
                </Flex>
            </Form>
            <div style={{display: "flex", flex: 1, width: '50%', gap: 20, flexDirection: "column"}}>
                {((journal?.group?.category?.nameEng === 'General English' || journal?.group?.category?.nameEng === 'Mathematics') && journal?.lessonWithDate === journal?.lessons?.length) &&
                    <Popconfirm
                        title={t('finishLessons')}
                        description={t('youSureToFinishLessons')}
                        onConfirm={finishAllLessons}
                        // className={classes.deleteBlock}
                        okText={t('yes')}
                        cancelText={t('no')}
                    >
                        <Button type={"primary"} style={{justifySelf: "flex-end"}}>
                            <IntlMessage id={'finishLessons'}/></Button>
                    </Popconfirm>
                }
                {(journal?.group?.category?.nameEng !== 'General English' && journal?.group?.category?.nameEng !== 'Mathematics' && journal?.group?.category?.nameEng !== 'Foundation') &&
                    <Button type={"primary"} style={{justifySelf: "flex-end"}}
                            onClick={() => setOpenAddLessonModal(true)}>
                        <IntlMessage id={'tg.addLesson'}/>
                    </Button>
                }
                {(journal?.group?.category?.nameEng === 'Foundation' && user?.role.roleName === 'admin') &&
                    <Flex justify={"space-around"} style={{width:'100%'}} gap={"small"}>
                        <Upload {...propsExcelFile} fileList={uploadedFile ? [uploadedFile] : []}>
                            <GreenButton>
                                <IntlMessage id={'certificate.excelFormat'}/>
                            </GreenButton>
                        </Upload>
                        <GreyButton disabled={uploadedFile == null} onClick={handleImportExcelFile}>
                            <IntlMessage id={'upload'}/>
                        </GreyButton>
                    </Flex>
                }
                <Form form={form} component={false}>
                    <Table
                        bordered
                        components={{
                            body: {cell: EditableCell},
                        }}
                        className={styles.customTable}
                        pagination={false}
                        columns={mergedColumns}
                        dataSource={dataSource2}
                        scroll={{x: true}}
                        size="small"
                    />
                </Form>
            </div>
            <Modal
                width={1000}
                maskClosable={true}
                title={<IntlMessage id={'tg.addLesson'}/>}
                open={openAddLessonModal}
                okText={<IntlMessage id={'save'}/>}
                cancelText={<IntlMessage id={'close'}/>}
                onOk={() => {
                    setOpenAddLessonModal(false);
                }}
                onCancel={() => {
                    setOpenAddLessonModal(false);
                }}
                footer={
                    <Button key="submit" type="primary" onClick={saveLessonsToJournal}>
                        <IntlMessage id={"save"}/>
                    </Button>
                }
            >
                {
                    openAddLessonModal &&
                    <Form form={lessonAddForm} className={classes.form} style={{overflowY: 'auto', maxHeight: 500}}>
                        <Form.List name="lessonsForJournals" rules={[{required: true, message: t('requiredField')}]}>
                            {(fields, {add, remove}) => (
                                <Flex vertical gap={10} style={{width: '100%'}}>
                                    {fields.map(({key, name, ...restField}, index) => {
                                        // Динамически вычисляем номер урока
                                        // const lessonNumber = (journal?.lessons?.length || 0) + index + 1;

                                        return (
                                            <Flex key={key} gap={"small"} justify={"flex-start"} align={"baseline"}
                                                  vertical
                                                  style={{
                                                      width: "100%",
                                                      padding: 8,
                                                      border: '1px solid white',
                                                      borderRadius: 8,
                                                      backgroundColor: "white"
                                                  }}>
                                                {/*<Form.Item*/}
                                                {/*    {...restField}*/}
                                                {/*    name={[name, "lessonNumberAddLesson"]}*/}
                                                {/*    className={classes.formInputBlock}*/}
                                                {/*    label={t('tg.lessonNumber')}*/}
                                                {/*    initialValue={lessonNumber}*/}
                                                {/*>*/}
                                                {/*    <Input*/}
                                                {/*        type={'number'}*/}
                                                {/*        className={classes.formInput}*/}
                                                {/*    />*/}
                                                {/*</Form.Item>*/}
                                                <Form.Item {...restField} name={[name, "topicNameAddLesson"]}
                                                           className={classes.formInputBlock}
                                                           label={t('tg.lessonTopic')}>
                                                    <Input className={classes.formInput}/>
                                                </Form.Item>
                                                <Form.Item {...restField} name={[name, "moduleNameAddLesson"]}
                                                           className={classes.formInputBlock}
                                                           label={t('tg.moduleOfLesson')}>
                                                    <Input className={classes.formInput}/>
                                                </Form.Item>
                                                <Form.Item {...restField} name={[name, "numberOfHoursAddLesson"]}
                                                           className={classes.formInputBlock} label={t('numOfHours')}>
                                                    <Input type={'number'} className={classes.formInput}/>
                                                </Form.Item>
                                                <Form.Item {...restField} name={[name, "lessonDateAddLesson"]}
                                                           className={classes.formInputBlock}
                                                           label={t('tg.lessonDate')}>
                                                    <Input type={'date'}/>
                                                </Form.Item>
                                                <Button danger variant="filled" onClick={() => remove(name)} block>
                                                    <IntlMessage id={"delete"}/>
                                                </Button>
                                            </Flex>
                                        );
                                    })}
                                    <Form.Item>
                                        <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined/>}>
                                            <IntlMessage id={"certificate.addField"}/>
                                        </Button>
                                    </Form.Item>
                                </Flex>
                            )}
                        </Form.List>
                    </Form>
                }
            </Modal>
            <Modal
                width={400}
                maskClosable={true}
                title={<IntlMessage id={'noteLesson'}/>}
                open={openNoteLessonModal}
                okText={<IntlMessage id={'save'}/>}
                cancelText={<IntlMessage id={'close'}/>}
                onOk={() => {
                    setOpenNoteLessonModal(false);
                    setNoteLesson(null);
                    setIsViewNote(false)
                }}
                onCancel={() => {
                    setOpenNoteLessonModal(false);
                    setNoteLesson(null);
                    setIsViewNote(false)
                }}
                footer={!isViewNote && [
                    // fix joinDate: пустой reason на бэке снимает уважительную причину —
                    // и уходит туда ТОЛЬКО по этой кнопке
                    noteLesson?.isAbsentForReason ? (
                        <Button key="clearReason" danger onClick={() => makeAbsentForReason('')}>
                            Снять причину
                        </Button>
                    ) : null,
                    // fix joinDate: явное «Закрыть» — чтобы выйти из заметки, ничего не отправляя
                    <Button key="close" onClick={() => {
                        setOpenNoteLessonModal(false);
                        setNoteLesson(null);
                        setIsViewNote(false);
                        reasonForm.resetFields();
                    }}>
                        <IntlMessage id={"close"}/>
                    </Button>,
                    <Button key="submit" type="primary" onClick={() => makeAbsentForReason()}>
                        <IntlMessage id={"save"}/>
                    </Button>
                ]}
            >
                {
                    openNoteLessonModal && (noteLesson || isViewNote) &&
                    <Form form={reasonForm} className={classes.form}>
                        <Form.Item name="reason" className={classes.formInputBlock}>
                            <Input.TextArea className={classes.formInput} disabled={isViewNote}/>
                        </Form.Item>
                    </Form>
                }
            </Modal>
            {/* Отчисление (admin): дата (по умолчанию сегодня) + необязательная причина */}
            <Modal
                width={420}
                maskClosable={true}
                title={<>{t('groups.expelTitle')}{expelStudent?.name ? `: ${expelStudent.name}` : ''}</>}
                open={!!expelStudent}
                okText={<IntlMessage id={'groups.expel'}/>}
                cancelText={<IntlMessage id={'cancel'}/>}
                okButtonProps={{danger: true, loading: isExpelSaving}}
                onOk={saveExpel}
                onCancel={() => {
                    setExpelStudent(null);
                    expelForm.resetFields();
                }}
            >
                <Form form={expelForm} layout="vertical">
                    <Form.Item name="leaveDate" label={t('groups.leaveDate')}
                               rules={[{required: true, message: t('requiredField')}]}
                               extra={t('groups.leaveDateHint')}>
                        {/* будущую дату бэкенд отклоняет: отчисление проводится в день ухода */}
                        <DatePicker format="DD.MM.YYYY" style={{width: '100%'}}
                                    disabledDate={d => d && d.isAfter(dayjs(), 'day')}/>
                    </Form.Item>
                    <Form.Item name="leaveReason" label={t('groups.leaveReason')}>
                        <Input.TextArea rows={3} maxLength={255} showCount/>
                    </Form.Item>
                </Form>
            </Modal>
            {/* п.10: полное расписание ученика по всем дисциплинам (клик по ФИО) */}
            <StudentScheduleModal
                open={openSchedule}
                onClose={() => setOpenSchedule(false)}
                studentId={scheduleStudent?.id}
                studentName={scheduleStudent?.name}
            />
        </div>
    );
};

export default JournalPage;